import { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { queryKeys } from './query-client';
import { resolveMount } from './resolve-mount';

/**
 * The caller's country from their IP, served by /api/geo off the edge geo header.
 *
 * Scoped to the **Symphony root mount** for now: the two manage.wix.com mounts keep the
 * browser-locale default they ship today, so this can't move Orion's onboarding. Reads the mount
 * from the URL rather than React state, which would lag a post-mount effect
 * flips it, which would skip the fetch on the render that matters.
 */
function geoCountryEnabled(): boolean {
  return typeof window !== 'undefined' && resolveMount(window.location.pathname) === '';
}

/**
 * Plain `fetch`, not `apiFetch`, and the only call in the data layer that opts out. /api/geo reads a
 * header and needs no identity, while `apiFetch` under the instance-auth toggle can redirect to
 * sign-in or throw on a failed instance acquisition — neither of which a country prefill may ever do
 * to someone mid-onboarding. It also awaits the toggle's own round-trip first, which would eat the
 * head start `prefetchGeoCountry` exists to buy.
 */
async function fetchGeoCountry(): Promise<string> {
  const res = await fetch(`${API_BASE}/api/geo`, { credentials: 'omit' });
  if (!res.ok) throw new Error(`geo lookup failed: ${res.status}`);
  const data = (await res.json()) as { countryCode?: string };
  return data.countryCode || '';
}

/**
 * The country the SERVER already knew, seeded from the edge header in `_app`'s
 * getInitialProps and shipped in `__NEXT_DATA__` — the same trick `ssrMessages` uses to
 * avoid an English→target flicker. It matters beyond flicker: a geo answer landing late
 * can swap the dial code under someone who has already started typing digits.
 * `''` = the server had no country (dev, or an unmapped IP), and the client fetch covers it.
 *
 * Deliberately NOT fed to the query below as `initialData`. `queryClient` is module-scope,
 * so on the server there is ONE cache per Node process shared by every request in flight:
 * seeding it wrote the first visitor's country into the cache and every later visitor's SSR
 * HTML rendered that stranger's country until it expired. A context value is per-render, so
 * it cannot leak across requests, and it is identical on the client's first render because
 * it travels in `__NEXT_DATA__` — so hydration still matches.
 *
 * It is the FIRST-PAINT value only, never the final word: the lookup below still runs and
 * still wins. The seed rides in the HTML document, which — unlike /api/geo, whose response is
 * explicitly `private, no-store` — carries no cache directive of its own, so a cached document
 * would hand one visitor's country to another. Letting the seed also SUPPRESS the lookup would
 * turn that into a number the user cannot get corrected: `usePhoneVerification` builds the
 * E.164 value from this country, so a wrong one addresses the verification SMS to the wrong
 * place. Keeping the lookup enabled costs one cheap request and makes a bad seed self-healing.
 */
export const SsrCountryContext = createContext<string>('');

/**
 * `''` until it resolves, and `''` for good whenever the edge has no country for the caller (an
 * unmapped IP) or the lookup fails — so every consumer treats "we don't know" as one case and keeps
 * its own fallback. A visitor's country doesn't change mid-session, so this never goes stale.
 */
export function useGeoCountry(): string {
  const ssrCountry = useContext(SsrCountryContext);
  const query = useQuery({
    queryKey: queryKeys.geoCountry,
    queryFn: fetchGeoCountry,
    enabled: geoCountryEnabled(),
    staleTime: Infinity,
  });
  // The lookup wins once it lands; the seed only covers the first paint. `''` from an unmapped
  // IP is falsy, so it falls back to the seed rather than clearing a good value.
  return query.data || ssrCountry || '';
}

